import {ResizeDirections, SectionModuleTypeEnum} from "@/scripts/common/enums.js";

function getAlignmentToGridMap(moduleType) {
    const alignmentToGridMap = {
        left: {
            module: {columnStart: 1, columnEnd: 9,},
            spacer: {columnStart: 1, columnEnd: 1,}
        },
        center: {
            module: {columnStart: 3, columnEnd: 11},
            spacer: {columnStart: null, columnEnd: null,}
        },
        right: {
            module: {columnStart: 5, columnEnd: 13,},
            spacer: {columnStart: 13, columnEnd: 13,}
        }
    }
    const map = JSON.parse(JSON.stringify(alignmentToGridMap));

    if (moduleType === SectionModuleTypeEnum.MEDIA) {
        map.center.module.columnStart = 1;
        map.center.module.columnEnd   = 13;
    }

    return map;
}

function deriveColumnSpan(columnStart, columnEnd) {
    if (typeof columnStart !== "number" || typeof columnEnd !== "number") {
        return 0;
    }

    return Math.max(0, columnEnd - columnStart);
}

export function createTextModule(columnStart, columnEnd, maxColSpan, minColSpan = 3) {
    return {
        moduleId: "text",
        type: SectionModuleTypeEnum.TEXT,
        gridSettings: {
            columnStart: columnStart,
            columnEnd: columnEnd,
            columnSpan: deriveColumnSpan(columnStart, columnEnd),
        },
        resizeSettings: {
            minColumnSpan: minColSpan,
            maxColumnSpan: maxColSpan,
            resizeDirections: ResizeDirections.HORIZONTAL,
            autoHeight: true
        },
    }
}

export function createMediaModule(columnStart, columnEnd, maxColSpan, minColSpan = 2) {
    return {
        moduleId: "media",
        type: SectionModuleTypeEnum.MEDIA,
        gridSettings: {
            columnStart: columnStart,
            columnEnd: columnEnd,
            columnSpan: deriveColumnSpan(columnStart, columnEnd),
        },
        resizeSettings: {
            minColumnSpan: minColSpan,
            maxColumnSpan: maxColSpan,
            resizeDirections: ResizeDirections.ALL,
            autoHeight: true
        }
    }
}

export function createContactFormModule(columnStart, columnEnd, maxColSpan) {
    return {
        moduleId: "contact-form",
        type: SectionModuleTypeEnum.CONTACT_FORM,
        gridSettings: {
            columnStart: columnStart,
            columnEnd: columnEnd,
            columnSpan: deriveColumnSpan(columnStart, columnEnd),
        },
        resizeSettings: {
            minColumnSpan: 4,
            maxColumnSpan: maxColSpan,
            resizeDirections: ResizeDirections.HORIZONTAL,
            autoHeight: true
        }
    }
}

export function createAccordionModule(columnStart, columnEnd, maxColSpan) {
    return {
        moduleId: "accordion",
        type: SectionModuleTypeEnum.ACCORDION,
        gridSettings: {
            columnStart: columnStart,
            columnEnd: columnEnd,
            columnSpan: deriveColumnSpan(columnStart, columnEnd),
        },
        resizeSettings: {
            minColumnSpan: 4,
            maxColumnSpan: maxColSpan,
            resizeDirections: ResizeDirections.HORIZONTAL,
            autoHeight: true
        }
    }
}

export function createSpacerModule(columnStart, columnEnd, maxColSpan = 12) {
    return {
        moduleId: `spacer-${crypto.randomUUID()}`,
        type: SectionModuleTypeEnum.SPACER,
        gridSettings: {
            columnStart: columnStart,
            columnEnd: columnEnd,
            columnSpan: deriveColumnSpan(columnStart, columnEnd),
        },
        resizeSettings: {
            minColumnSpan: 0,
            maxColumnSpan: maxColSpan,
            resizeDirections: ResizeDirections.SINGLE
        }
    }
}

export const LAYOUT_TYPES = {
    TEXT_AND_MEDIA_ORIGINAL: 'TEXT_AND_MEDIA_ORIGINAL',
    MOCKUP_LAYOUT: 'MOCKUP_LAYOUT',
    MOCKUP_LAYOUT_FLIPPED: 'MOCKUP_LAYOUT_FLIPPED',
    MEDIA_FULL_WIDTH: 'MEDIA_FULL_WIDTH',
    TEXT_DEFAULT: 'TEXT_DEFAULT',
    ACCORDION_AND_TEXT: 'ACCORDION_AND_TEXT',
    ACCORDION_AND_IMAGE: 'ACCORDION_AND_IMAGE',
    ACCORDION_FULL_WIDTH: 'ACCORDION_FULL_WIDTH',
    CONTACT_FORM_ORIGINAL: 'CONTACT_FORM_ORIGINAL',
    CONTACT_FORM_AND_TEXT: 'CONTACT_FORM_AND_TEXT',
    CONTACT_FORM_CENTERED: 'CONTACT_FORM_CENTERED',
    HERO: 'HERO',
    HERO_REVERTED: 'HERO_REVERTED',
    HERO_MODULAR_VERTICAL: 'HERO_MODULAR_VERTICAL',
    HERO_OVERLAY: 'HERO_OVERLAY',
    HERO_SIDEBAR: 'HERO_SIDEBAR',
    HERO_SIDEBAR_TEXT: 'HERO_SIDEBAR_TEXT',
};

export const LAYOUT_CONFIGURATIONS = {
    [LAYOUT_TYPES.TEXT_AND_MEDIA_ORIGINAL]: () => ({
        SPACER_START: createSpacerModule(1, 1, 7),
        MEDIA: createMediaModule(1, 7, 9),
        SPACER_INLINE: createSpacerModule(7, 8, 7),
        TEXT: createTextModule(8, 13, 10),
        SPACER_END: createSpacerModule(13, 13, 7),
    }),
    [LAYOUT_TYPES.MEDIA_FULL_WIDTH]: () => ({
        MEDIA: createMediaModule(1, 13, 12),
    }),

    [LAYOUT_TYPES.TEXT_DEFAULT]: () => ({
        SPACER_START: createSpacerModule(1, 1, 9),
        TEXT: createTextModule(1, 9, 12),
    }),

    [LAYOUT_TYPES.ACCORDION_AND_TEXT]: () => ({
        SPACER_START: createSpacerModule(1, 1, 6),
        TEXT: createTextModule(1, 7, 8),
        SPACER_INLINE: createSpacerModule(7, 7, 6),
        ACCORDION: createAccordionModule(7, 13, 12),
        SPACER_END: createSpacerModule(13, 13, 6),
    }),

    [LAYOUT_TYPES.ACCORDION_AND_IMAGE]: () => ({
        SPACER_START: createSpacerModule(1, 1, 6),
        MEDIA: createMediaModule(1, 7, 8),
        SPACER_INLINE: createSpacerModule(7, 7, 6),
        ACCORDION: createAccordionModule(7, 13, 12),
        SPACER_END: createSpacerModule(13, 13, 6),
    }),

    [LAYOUT_TYPES.ACCORDION_FULL_WIDTH]: () => ({
        SPACER_START: createSpacerModule(1, 1, 8),
        ACCORDION: createAccordionModule(1, 13, 12),
    }),

    [LAYOUT_TYPES.CONTACT_FORM_ORIGINAL]: () => ({
        SPACER_START: createSpacerModule(1, 1, 6),
        MEDIA: createMediaModule(1, 6, 8),
        SPACER_INLINE: createSpacerModule(6, 7, 6),
        CONTACT_FORM: createContactFormModule(7, 13, 8),
        SPACER_END: createSpacerModule(13, 13, 6),
    }),

    [LAYOUT_TYPES.CONTACT_FORM_AND_TEXT]: () => ({
        SPACER_START: createSpacerModule(1, 1, 6),
        TEXT: createTextModule(1, 7, 8),
        SPACER_INLINE: createSpacerModule(7, 7, 6),
        CONTACT_FORM: createContactFormModule(7, 13, 8),
        SPACER_END: createSpacerModule(13, 13, 6),
    }),

    [LAYOUT_TYPES.CONTACT_FORM_CENTERED]: () => ({
        CONTACT_FORM: createContactFormModule(3, 11, 8),
    }),

    [LAYOUT_TYPES.HERO]: () => ({
        SPACER_START: createSpacerModule(1, 1, 7),
        MEDIA: createMediaModule(1, 5, 9),
        SPACER_INLINE: createSpacerModule(5, 5, 7),
        TEXT: createTextModule(5, 13, 10),
        SPACER_END: createSpacerModule(13, 13, 7),
    }),

    [LAYOUT_TYPES.HERO_REVERTED]: () => ({
        SPACER_START: createSpacerModule(1, 1, 7),
        TEXT: createTextModule(1, 9, 10),
        SPACER_INLINE: createSpacerModule(9, 9, 7),
        MEDIA: createMediaModule(9, 13, 9),
        SPACER_END: createSpacerModule(13, 13, 7),
    }),

    [LAYOUT_TYPES.HERO_MODULAR_VERTICAL]: () => {
        const media = createMediaModule(1, 13, 12);
        const text = createTextModule(1, 13, 12);
        return {
            MEDIA: {
                ...media,
                gridSettings: {
                    ...media.gridSettings,
                    rowStart: 1,
                    rowEnd: 2,
                    columnSpan: 12,
                },
            },
            TEXT: {
                ...text,
                gridSettings: {
                    ...text.gridSettings,
                    rowStart: 2,
                    rowEnd: 3,
                    columnSpan: 12,
                },
            },
        };
    },

    [LAYOUT_TYPES.HERO_OVERLAY]: () => {
        const media = createMediaModule(1, 13, 12);
        const text = createTextModule(3, 11, 12);
        return {
            MEDIA: {
                ...media,
                resizeSettings: {
                    ...media.resizeSettings,
                    minColumnSpan: 12,
                    isLocked: true,
                },
            },
            TEXT: text,
        };
    },

    [LAYOUT_TYPES.HERO_SIDEBAR]: () => {
        const media = createMediaModule(1, 5, 4, 2);
        const text = createTextModule(1, 5, 4, 4);
        return {
            MEDIA: {
                ...media,
                gridSettings: {
                    ...media.gridSettings,
                    rowStart: 1,
                    rowEnd: 2,
                    columnSpan: 4,
                },
            },
            TEXT: {
                ...text,
                gridSettings: {
                    ...text.gridSettings,
                    rowStart: 2,
                    rowEnd: 3,
                    columnSpan: 4,
                },
                resizeSettings: {
                    ...text.resizeSettings,
                    isLocked: true,
                },
            },
        };
    },

    [LAYOUT_TYPES.HERO_SIDEBAR_TEXT]: () => {
        const text = createTextModule(1, 5, 4);
        return {
            TEXT: {
                ...text,
                gridSettings: {
                    ...text.gridSettings,
                    rowStart: 1,
                    rowEnd: 2,
                    columnSpan: 4,
                },
                resizeSettings: {
                    ...text.resizeSettings,
                    isLocked: true,
                },
            },
        };
    },

    [LAYOUT_TYPES.MOCKUP_LAYOUT]: () => {
        const media = createMediaModule(1, 6, 8);
        return {
            SPACER_START: createSpacerModule(1, 1, 7),
            MEDIA: {
                ...media,
                resizeSettings: {
                    ...media.resizeSettings,
                    minColumnSpan: 5,
                    resizeDirections: ResizeDirections.HORIZONTAL,
                    autoHeight: true,
                }
            },
            SPACER_INLINE: createSpacerModule(6, 6, 7),
            TEXT: createTextModule(6, 13, 10),
            SPACER_END: createSpacerModule(13, 13, 7),
        };
    },

    [LAYOUT_TYPES.MOCKUP_LAYOUT_FLIPPED]: () => {
        const media = createMediaModule(8, 13, 8);
        return {
            SPACER_START: createSpacerModule(1, 1, 7),
            TEXT: createTextModule(1, 8, 10),
            SPACER_INLINE: createSpacerModule(8, 8, 7),
            MEDIA: {
                ...media,
                resizeSettings: {
                    ...media.resizeSettings,
                    minColumnSpan: 5,
                    resizeDirections: ResizeDirections.HORIZONTAL,
                    autoHeight: true,
                }
            },
            SPACER_END: createSpacerModule(13, 13, 7),
        };
    },
};

export function applyLayoutConfiguration(modules, configName) {
    const configFactory = LAYOUT_CONFIGURATIONS[configName];
    if (!configFactory) return modules;

    const config = configFactory();
    return Object.values(config);
}

export function createLayoutPatch(layoutSettings, configName) {
    return {
        ...layoutSettings,
        modules: applyLayoutConfiguration(layoutSettings.modules, configName),
    };
}

const TYPE_DEFAULTS = {
    [SectionModuleTypeEnum.TEXT]:         { min: 3, max: 12, dir: ResizeDirections.HORIZONTAL },
    [SectionModuleTypeEnum.TITLE]:       { min: 3, max: 12, dir: ResizeDirections.HORIZONTAL },
    [SectionModuleTypeEnum.ACCORDION]:   { min: 4, max: 12, dir: ResizeDirections.HORIZONTAL },
    [SectionModuleTypeEnum.CONTACT_FORM]: { min: 4, max: 8, dir: ResizeDirections.HORIZONTAL },
    [SectionModuleTypeEnum.MEDIA]:        { min: 2, max: 12, dir: ResizeDirections.ALL },
    [SectionModuleTypeEnum.MOCKUP_LAYOUT]: {}
};

export function setHorizontalAlignment(modules, horizontalAlignment) {
    const withoutSpacers = modules.filter(m => m.type !== SectionModuleTypeEnum.SPACER);
    const primaryModule  = withoutSpacers.find(m => m.type !== SectionModuleTypeEnum.SPACER) ?? withoutSpacers[0];
    if (!primaryModule) return modules;

    const map = getAlignmentToGridMap(primaryModule.type);
    const { module, spacer } = map[horizontalAlignment];

    const tdef = TYPE_DEFAULTS[primaryModule.type] ?? { min: 3, max: 12, dir: ResizeDirections.HORIZONTAL };
    const minForAlign = (horizontalAlignment !== 'center') ? tdef.min : Math.max(tdef.min, 4);

    const updatedModule = {
        ...primaryModule,
        gridSettings: {
            ...primaryModule.gridSettings,
            columnStart: module.columnStart,
            columnEnd: module.columnEnd,
            columnSpan: deriveColumnSpan(module.columnStart, module.columnEnd),
        },
        resizeSettings: {
            ...primaryModule.resizeSettings,
            resizeDirections: tdef.dir,
            minColumnSpan: minForAlign,
            maxColumnSpan: tdef.max,
        }
    };

    if (horizontalAlignment === 'left') {
        const newSpacer = createSpacerModule(1, 1, 12);
        return [newSpacer, updatedModule];
    } else if (horizontalAlignment === 'right') {
        const newSpacer = createSpacerModule(13, 13, 12);
        return [updatedModule, newSpacer];
    } else { // center
        return [updatedModule];
    }
}
